class QuizAnalytics {
  constructor(quizElement) {
    this.quizElement = quizElement;
    this.quizId = quizElement.id;
    this.totalSteps = parseInt(quizElement.dataset.totalSteps) || 0;
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
    this.currentStep = 1;
    this.stepTimings = {};
    
    // Get test configuration from quiz element
    this.testConfig = {
      active: quizElement.dataset.testActive === 'true',
      uniqueTestId: quizElement.dataset.uniqueTestId || 'no_test',
      userVariant: quizElement.dataset.userVariant || 'control',
      testPercentage: parseInt(quizElement.dataset.testPercentage) || 0,
      testStartDate: quizElement.dataset.testStartDate || ''
    };
    
    this.initializeGA4();
    this.bindEvents();
    
    console.log('Quiz Analytics initialized:', {
      quizId: this.quizId,
      testConfig: this.testConfig,
      sessionId: this.sessionId
    });
  }
  
  initializeGA4() {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function() {
      dataLayer.push(arguments);
    };
  }
  
  generateSessionId() {
    return `quiz_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  bindEvents() {
    // Track cross-sell clicks
    this.quizElement.addEventListener('click', (e) => {
      if (e.target.matches('.cross-sell-add-button')) {
        this.trackCrossSellClick(e.target);
      }
    });
    
    // Track quiz completion
    this.quizElement.addEventListener('quiz:complete', (e) => {
      this.trackQuizComplete(e.detail);
    });
    
    // Track cart add success
    this.quizElement.addEventListener('cart-add-success', (e) => {
      this.trackCartAdd(e.detail);
    });
    
    // Track cart add error
    this.quizElement.addEventListener('cart-add-error', (e) => {
      this.trackError('cart_add_error', e.detail);
    });
    
    // Track state changes
    this.quizElement.addEventListener('state-changed', (e) => {
      this.trackStateChange(e.detail);
    });
    
    // Track page unload for abandonment
    window.addEventListener('beforeunload', () => {
      const currentStep = parseInt(this.quizElement.state?.currentStep) || this.currentStep;
      if (currentStep < this.totalSteps) {
        this.trackQuizAbandon(currentStep);
      }
    });
  }
  
  trackQuizStart() {
    const eventData = {
      event: 'quiz_start',
      quiz_id: this.quizId,
      session_id: this.sessionId,
      total_steps: this.totalSteps,
      test_active: this.testConfig.active,
      test_id: this.testConfig.uniqueTestId,
      user_variant: this.testConfig.userVariant,
      test_percentage: this.testConfig.testPercentage,
      test_start_date: this.testConfig.testStartDate,
      timestamp: new Date().toISOString()
    };
    
    gtag('event', 'quiz_start', eventData);
    
    console.log('📊 Quiz Started:', eventData);
  }
  
  trackStepView(stepNumber, stepType, stepId, variantInfo = {}) {
    // Track time spent on previous step
    if (this.currentStep !== stepNumber && this.stepTimings[this.currentStep]) {
      const timeOnStep = Date.now() - this.stepTimings[this.currentStep];
      this.trackTimeOnStep(this.currentStep, timeOnStep);
    }
    
    this.currentStep = stepNumber;
    this.stepTimings[stepNumber] = Date.now();
    
    const eventData = {
      quiz_id: this.quizId,
      session_id: this.sessionId,
      step_number: stepNumber,
      step_type: stepType,
      step_id: stepId,
      time_on_quiz: Math.round((Date.now() - this.startTime) / 1000),
      
      // Global test info
      quiz_test_active: this.testConfig.active,
      quiz_test_id: this.testConfig.uniqueTestId,
      quiz_user_variant: this.testConfig.userVariant,
      
      // Step-specific test info
      step_test_active: variantInfo.testActive || false,
      step_variant: variantInfo.variant || 'control',
      step_variant_group: variantInfo.variantGroup || 'none'
    };
    
    gtag('event', 'quiz_step_view', eventData);
    
    // Enhanced logging for A/B tests
    if (variantInfo.testActive) {
      console.log(`📊 A/B Test Step View:`, {
        step: `${stepType} (Step ${stepNumber})`,
        variantGroup: variantInfo.variantGroup,
        variantShown: variantInfo.variant,
        globalUserVariant: this.testConfig.userVariant,
        timeOnQuiz: `${eventData.time_on_quiz}s`
      });
    }
    
    // Special handling for cross-sell steps
    if (stepType === 'cross-sell') {
      this.trackCrossSellView(stepId, variantInfo);
    }
  }
  
  trackTimeOnStep(stepNumber, timeSpent) {
    gtag('event', 'quiz_step_time', {
      quiz_id: this.quizId,
      session_id: this.sessionId,
      step_number: stepNumber,
      time_spent_ms: timeSpent,
      time_spent_seconds: Math.round(timeSpent / 1000)
    });
  }
  
  trackCrossSellView(stepId, variantInfo) {
    const step = this.quizElement.querySelector(`.quiz-step[data-block-id="${stepId}"]`);
    const crossSellProduct = step?.querySelector('.cross-sell-product');
    
    if (!crossSellProduct) return;
    
    const eventData = {
      quiz_id: this.quizId,
      session_id: this.sessionId,
      step_id: stepId,
      test_active: variantInfo.testActive,
      variant_group: variantInfo.variantGroup,
      variant: variantInfo.variant,
      product_id: crossSellProduct.dataset.productId,
      product_handle: crossSellProduct.dataset.productHandle,
      product_title: crossSellProduct.dataset.productTitle,
      product_price: parseFloat(crossSellProduct.dataset.productPrice),
      currency: window.Shopify?.currency?.active || 'USD',
      time_on_quiz: Math.round((Date.now() - this.startTime) / 1000)
    };
    
    gtag('event', 'cross_sell_view', eventData);
    
    // Also send standard GA4 ecommerce event
    gtag('event', 'view_item', {
      currency: eventData.currency,
      value: eventData.product_price,
      items: [{
        item_id: eventData.product_id,
        item_name: eventData.product_title,
        price: eventData.product_price,
        item_category: 'cross-sell',
        item_variant: variantInfo.testActive ? variantInfo.variant : 'no_test'
      }]
    });
  }
  
  trackCrossSellClick(button) {
    const step = button.closest('.quiz-step');
    const product = button.closest('.cross-sell-product');
    
    if (!step || !product) return;
    
    const variantGroup = step.dataset.variantGroup;
    const variant = step.dataset.variantType;
    const stepId = step.dataset.blockId;
    
    const eventData = {
      quiz_id: this.quizId,
      session_id: this.sessionId,
      step_id: stepId,
      variant_group: variantGroup || 'none',
      variant: variant || 'control',
      test_active: !!variantGroup,
      product_id: product.dataset.productId,
      product_handle: product.dataset.productHandle,
      product_title: product.dataset.productTitle,
      product_price: parseFloat(product.dataset.productPrice),
      currency: window.Shopify?.currency?.active || 'USD',
      click_time: Math.round((Date.now() - this.startTime) / 1000)
    };
    
    gtag('event', 'cross_sell_click', eventData);
    
    // Standard GA4 ecommerce event
    gtag('event', 'add_to_cart', {
      currency: eventData.currency,
      value: eventData.product_price,
      items: [{
        item_id: eventData.product_id,
        item_name: eventData.product_title,
        price: eventData.product_price,
        quantity: 1,
        item_category: 'cross-sell',
        item_variant: eventData.test_active ? eventData.variant : 'no_test'
      }]
    });
    
    console.log('🛒 Cross-sell clicked:', eventData);
  }
  
  trackInteraction(interactionType, details) {
    const eventData = {
      quiz_id: this.quizId,
      session_id: this.sessionId,
      interaction_type: interactionType,
      current_step: this.currentStep,
      time_on_quiz: Math.round((Date.now() - this.startTime) / 1000),
      test_active: this.testConfig.active,
      user_variant: this.testConfig.userVariant,
      ...details
    };
    
    gtag('event', 'quiz_interaction', eventData);
  }
  
  trackStateChange(details) {
    // Only track significant state changes
    const significantKeys = [
      'selectedMainVariantId', 
      'subscriptionType', 
      'crossSellProducts',
      'currentStep'
    ];
    
    if (significantKeys.includes(details.key)) {
      this.trackInteraction('state_change', {
        state_key: details.key,
        state_value: details.value
      });
    }
  }
  
  trackQuizComplete(completionData = {}) {
    // Track time on final step
    if (this.stepTimings[this.currentStep]) {
      const timeOnStep = Date.now() - this.stepTimings[this.currentStep];
      this.trackTimeOnStep(this.currentStep, timeOnStep);
    }
    
    const eventData = {
      quiz_id: this.quizId,
      session_id: this.sessionId,
      total_time: Math.round((Date.now() - this.startTime) / 1000),
      steps_completed: this.totalSteps,
      test_active: this.testConfig.active,
      test_id: this.testConfig.uniqueTestId,
      user_variant: this.testConfig.userVariant,
      ...completionData
    };
    
    gtag('event', 'quiz_complete', eventData);
    
    // Track conversion if test is active
    if (this.testConfig.active) {
      gtag('event', 'ab_test_conversion', {
        test_id: this.testConfig.uniqueTestId,
        variant: this.testConfig.userVariant,
        quiz_id: this.quizId,
        session_id: this.sessionId
      });
    }
    
    console.log('✅ Quiz Completed:', eventData);
  }
  
  trackQuizAbandon(currentStep) {
    const eventData = {
      quiz_id: this.quizId,
      session_id: this.sessionId,
      abandoned_at_step: currentStep,
      time_on_quiz: Math.round((Date.now() - this.startTime) / 1000),
      test_active: this.testConfig.active,
      test_id: this.testConfig.uniqueTestId,
      user_variant: this.testConfig.userVariant,
      completion_percentage: Math.round((currentStep / this.totalSteps) * 100)
    };
    
    gtag('event', 'quiz_abandon', eventData);
    
    console.log('❌ Quiz Abandoned:', eventData);
  }
  
  trackCartAdd(cartData) {
    const eventData = {
      quiz_id: this.quizId,
      session_id: this.sessionId,
      cart_items: cartData.items || [],
      total_value: cartData.total_price || 0,
      currency: cartData.currency || 'USD',
      test_active: this.testConfig.active,
      test_id: this.testConfig.uniqueTestId,
      user_variant: this.testConfig.userVariant
    };
    
    gtag('event', 'quiz_cart_add', eventData);
  }
  
  trackError(errorType, errorDetails) {
    gtag('event', 'quiz_error', {
      quiz_id: this.quizId,
      session_id: this.sessionId,
      error_type: errorType,
      error_details: JSON.stringify(errorDetails),
      current_step: this.currentStep,
      time_on_quiz: Math.round((Date.now() - this.startTime) / 1000)
    });
    
    console.error('Quiz Error:', errorType, errorDetails);
  }
}

// Export for use in quiz element
window.QuizAnalytics = QuizAnalytics;

// Auto-initialize if GA/gtag is present
if (window.gtag) {
  console.log('Quiz Analytics: GA4 detected and ready');
} else {
  console.warn('Quiz Analytics: GA4 (gtag) not found. Analytics will not be tracked.');
} 